import { HttpErrorResponse } from '@angular/common/http';

/**
 * The message an API rejection should be shown as.
 *
 * ⚠️ **Twin.** This is a copy of `apiErrorMessage` in
 * `projects/partner-area/src/app/features/content/categories/api-error.ts`,
 * which five app-side screens still import. A library cannot import a feature
 * helper out of an app (app → lib is the only allowed direction), and the app's
 * copy also carries `isStaleStateError`, which is a category-tree concern with
 * nothing to do with media. Keep the two in step, or promote the pair into a
 * shared library and delete both.
 *
 * Nest's exception filter serialises to `{ statusCode, message, error }`, where
 * `message` is either a string or an array of strings (class-validator). Both
 * shapes are surfaced verbatim, because these endpoints' 400s are the ones a
 * user can act on. A generic fallback would throw that away.
 *
 * Anything that isn't a recognisable API error (a network drop, a CORS failure)
 * falls back to the caller's own wording.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof HttpErrorResponse)) return fallback;

  // A status of 0 is the browser refusing to tell us why — offline, DNS, CORS.
  if (error.status === 0) return 'Please check your connection and try again.';

  const body: unknown = error.error;
  const message: unknown =
    body && typeof body === 'object' ? (body as Record<string, unknown>)['message'] : body;

  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message)) {
    const joined = message.filter((part): part is string => typeof part === 'string').join(' ');
    if (joined.trim()) return joined;
  }

  return fallback;
}
